import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { inboundPaymentsApi, type CommissionChange } from '../api/inbound-payments.api.js';
import { commissionInput, parseCommission } from '../utils/commission-money.js';
import { formatTransactionAmount as money } from '../utils/transaction-money.js';
import { APP_LOCALE } from '../utils/locale.js';

export function IncomingCommissionPanel({ paymentId, canEdit = false }: {
  paymentId: string;
  /** DEV-67: admin and the client manager set a commission before crediting; the MLRO sees it. */
  canEdit?: boolean;
}): JSX.Element {
  const cache = useQueryClient();
  const query = useQuery({
    queryKey: ['inbound-commission', paymentId],
    queryFn: () => inboundPaymentsApi.commission(paymentId),
  });
  const [draft, setDraft] = useState<null | {
    mode: 'edit' | 'correct';
    operationId: string;
    expectedRevision: number;
    expectedFeeCents: string;
    currency: string;
    amountCents: string;
  }>(null);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const refresh = async () => {
    await Promise.all([
      cache.invalidateQueries({ queryKey: ['inbound-commission', paymentId] }),
      cache.invalidateQueries({ queryKey: ['inbound-payment-detail', paymentId] }),
      cache.invalidateQueries({ queryKey: ['transactions'] }),
      cache.invalidateQueries({ queryKey: ['inbound-payments'] }),
      cache.invalidateQueries({ queryKey: ['commissions'] }),
    ]);
  };
  const change = useMutation({
    mutationFn: async (input: { draft: NonNullable<typeof draft>; input: CommissionChange }) => {
      if (input.draft.mode === 'edit')
        await inboundPaymentsApi.editFee(paymentId, input.input, input.draft.operationId);
      else await inboundPaymentsApi.correctFee(paymentId, input.input, input.draft.operationId);
    },
    onSuccess: async (_value, input) => {
      setDraft(null);
      setMessage(
        input.draft.mode === 'edit'
          ? 'Commission updated. It will be collected when the payment is released.'
          : 'Commission corrected. The adjustment is recorded in transaction history.',
      );
      await refresh();
    },
    onError: async (err) => {
      setDraft(null);
      setError(err instanceof Error ? err.message : 'The commission could not be confirmed.');
      await refresh();
    },
  });
  const resume = useMutation({
    mutationFn: (operationId: string) =>
      inboundPaymentsApi.resumeCommission(paymentId, operationId),
    onSuccess: async () => {
      setMessage('The saved operation is confirmed.');
      setError(null);
      await refresh();
    },
    onError: async (err) => {
      setError(
        err instanceof Error ? err.message : 'The operation is still awaiting confirmation.',
      );
      await refresh();
    },
  });
  const data = query.data;
  const open = (mode: 'edit' | 'correct') => {
    if (!data) return;
    setError(null);
    setMessage(null);
    setReason('');
    setAmount(commissionInput(data.feeCents, data.currency));
    setDraft({
      mode,
      operationId: crypto.randomUUID(),
      expectedRevision: mode === 'edit' ? data.editRevision : (data.posted?.revision ?? 0),
      expectedFeeCents: data.feeCents,
      currency: data.currency,
      amountCents: data.amountCents,
    });
  };
  let preview: bigint | null = null;
  let validation: string | null = null;
  if (draft) {
    try {
      preview = parseCommission(amount, draft.currency);
      if (preview > BigInt(draft.amountCents))
        validation = 'The commission cannot exceed the incoming amount.';
    } catch (err) {
      validation = err instanceof Error ? err.message : 'Enter a valid commission.';
    }
  }
  const delta = preview !== null && draft ? preview - BigInt(draft.expectedFeeCents) : null;
  return (
    <Box sx={{ px: { xs: 2.5, sm: 3 }, py: 2, borderTop: 1, borderColor: 'divider' }}>
      <Stack spacing={1.5}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
          <Typography variant="subtitle1" fontWeight={700}>
            Transaction commission
          </Typography>
          {data && (
            <Chip
              size="small"
              label={
                data.pendingOperationId
                  ? 'Awaiting confirmation'
                  : data.verificationError
                    ? 'Unverified'
                    : data.posted?.reversed
                      ? 'Refunded'
                      : data.posted
                        ? 'Posted'
                        : 'Before credit'
              }
            />
          )}
        </Stack>
        {query.isLoading && <LinearProgress aria-label="Loading commission" />}
        {query.isError && (
          <Alert
            severity="error"
            action={
              <Button
                onClick={() => {
                  void query.refetch();
                }}
              >
                Retry
              </Button>
            }
          >
            Commission details could not be loaded.
          </Alert>
        )}
        {message && (
          <Alert severity="success" onClose={() => setMessage(null)}>
            {message}
          </Alert>
        )}
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {data && (
          <>
            <Typography variant="h6">{money(data.feeCents, data.currency)}</Typography>
            {data.posted ? (
              <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                Recorded fee account: {data.posted.feeAccountId}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {data.verificationError
                  ? 'The displayed review amount has not been confirmed against the ledger.'
                  : data.overrideFeeCents !== null
                    ? 'Staff-edited commission. This amount will be used at release.'
                    : 'Tariff estimate. An edit fixes the commission for this payment before release.'}
              </Typography>
            )}
            {data.verificationError && <Alert severity="warning">{data.verificationError}</Alert>}
            {data.pendingOperationId ? (
              <Alert
                severity="warning"
                action={!canEdit ? undefined :
                  <Button
                    disabled={resume.isPending}
                    onClick={() => resume.mutate(data.pendingOperationId!)}
                  >
                    Confirm outcome
                  </Button>
                }
              >
                A saved operation is awaiting confirmation. It will reuse the original instruction.
              </Alert>
            ) : canEdit && data.status === 'PENDING_REVIEW' ? (
              <Button
                variant="outlined"
                onClick={() => open('edit')}
                sx={{ alignSelf: 'flex-start' }}
              >
                Edit commission before credit
              </Button>
            ) : canEdit && data.status === 'APPROVED' && data.posted && !data.posted.reversed ? (
              <Button
                variant="outlined"
                onClick={() => open('correct')}
                sx={{ alignSelf: 'flex-start' }}
              >
                Correct posted commission
              </Button>
            ) : null}
            {(data.history.length > 0 || (data.posted?.changes.length ?? 0) > 0) && (
              <Accordion disableGutters elevation={0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2">Commission history</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1.5}>
                    {data.history.map((item) => {
                      const postedChange = data.posted?.changes.find(
                        (c) => c.operationId === item.id,
                      );
                      return (
                        <Box key={item.id}>
                          <Typography variant="body2" fontWeight={600}>
                            {item.kind === 'EDIT'
                              ? 'Edit before credit'
                              : item.kind === 'CORRECT'
                                ? 'Posted correction'
                                : item.kind === 'REVERSE'
                                  ? 'Refund with reversal'
                                  : 'Initial collection'}{' '}
                            · {item.status}
                          </Typography>
                          {item.request && (
                            <Typography variant="body2">
                              {money(item.request.expectedFeeCents, data.currency)} →{' '}
                              {money(item.request.feeCents, data.currency)}
                            </Typography>
                          )}
                          {postedChange && (
                            <Typography variant="body2">
                              {money(postedChange.beforeCents, data.currency)} →{' '}
                              {money(postedChange.afterCents, data.currency)}
                            </Typography>
                          )}
                          <Typography variant="body2">{item.reason}</Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ overflowWrap: 'anywhere' }}
                          >
                            {new Date(item.createdAt).toLocaleString(APP_LOCALE)} · {item.officerId}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}
          </>
        )}
      </Stack>
      <Dialog
        open={draft !== null}
        onClose={() => {
          if (!change.isPending) setDraft(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <Box
          component="form"
          onSubmit={(event) => {
            event.preventDefault();
            if (!draft || preview === null || validation || reason.trim().length < 4) return;
            change.mutate({
              draft,
              input: {
                feeCents: preview.toString(),
                expectedFeeCents: draft.expectedFeeCents,
                expectedRevision: draft.expectedRevision,
                reason: reason.trim(),
              },
            });
          }}
        >
          <DialogTitle>
            {draft?.mode === 'edit' ? 'Edit commission before credit' : 'Correct posted commission'}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2">
                {draft?.mode === 'edit'
                  ? 'This changes the fee for this payment. No money moves until release.'
                  : 'The original charge remains in history. Only the difference will be charged or refunded to the recorded fee account.'}
              </Typography>
              <TextField
                label={`New commission (${draft?.currency ?? ''})`}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                inputProps={{ inputMode: 'decimal' }}
                required
                error={validation !== null}
                helperText={
                  validation ??
                  'Enter the final total commission, including any amount already charged.'
                }
              />
              {draft && preview !== null && !validation && (
                <>
                  <Divider />
                  <Typography variant="body2">
                    Total after commission:{' '}
                    <strong>
                      {money((BigInt(draft.amountCents) - preview).toString(), draft.currency)}
                    </strong>
                  </Typography>
                  {draft.mode === 'correct' && delta !== null && (
                    <Alert severity={delta > 0n ? 'warning' : 'info'}>
                      {delta > 0n ? 'Additional charge' : delta < 0n ? 'Refund' : 'No change'}:{' '}
                      {money((delta < 0n ? -delta : delta).toString(), draft.currency)}
                    </Alert>
                  )}
                </>
              )}
              <TextField
                label="Reason for commission change"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
                multiline
                minRows={2}
                inputProps={{ minLength: 4, maxLength: 1000 }}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button disabled={change.isPending} onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={
                change.isPending ||
                !!validation ||
                preview === null ||
                reason.trim().length < 4 ||
                (draft?.mode === 'correct' && delta === 0n)
              }
            >
              {change.isPending
                ? 'Saving…'
                : draft?.mode === 'edit'
                  ? 'Save commission'
                  : 'Post correction'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
}
